import { sd, type SdAppRole } from "@sd/lib/sdPaths";

export const TICKET_DETAIL_RETURN_TO = "returnTo";

/** Restores workbench split-pane selection when returning from full detail view. */
export const WORKBENCH_SELECTED_TICKET_QUERY = "ticket";

/** Dedicated read-only ticket detail page (Redmine-style). */
export function buildTicketDetailPath(
  ticketId: string,
  options?: { returnTo?: string }
): string {
  const id = ticketId.trim();
  if (!id) {
    return sd("tickets");
  }
  const path = sd(`tickets/view/${encodeURIComponent(id)}`);
  const returnTo = options?.returnTo?.trim();
  if (!returnTo) {
    return path;
  }
  const params = new URLSearchParams();
  params.set(TICKET_DETAIL_RETURN_TO, returnTo);
  return `${path}?${params.toString()}`;
}

export function parseTicketDetailReturnTo(
  searchParams: URLSearchParams
): string | null {
  const value = searchParams.get(TICKET_DETAIL_RETURN_TO)?.trim();
  return value || null;
}

/** Sidebar active queue from a `returnTo` path (e.g. filtered `/sd/tickets/all?...`). */
export function inferTicketQueueFromReturnTo(
  returnTo: string,
  role: SdAppRole | null
): string {
  const match = returnTo.match(/\/tickets\/([^/?]+)/);
  const segment = match?.[1];
  if (segment && segment !== "view" && segment !== "new") {
    return segment;
  }
  if (role === "ServiceDesk_SuperAgent") return "project-status";
  if (role === "ServiceDesk_Agent") return "my-tickets";
  return "all";
}

/** Append selected ticket id to a workbench list URL used as detail-page `returnTo`. */
export function appendWorkbenchSelectedTicketToPath(
  pathWithSearch: string,
  ticketId: string
): string {
  const base = pathWithSearch.trim();
  const id = ticketId.trim();
  if (!base || !id) return base;

  const qIndex = base.indexOf("?");
  const pathname = qIndex >= 0 ? base.slice(0, qIndex) : base;
  const params = new URLSearchParams(qIndex >= 0 ? base.slice(qIndex + 1) : "");
  params.set(WORKBENCH_SELECTED_TICKET_QUERY, id);
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export function parseWorkbenchSelectedTicketFromSearchParams(
  searchParams: URLSearchParams
): string | null {
  const id = searchParams.get(WORKBENCH_SELECTED_TICKET_QUERY)?.trim();
  return id || null;
}
